/**
 * API per Credit Service (Java - porta 8091)
 * Gestisce wallet, saldi, fido e operazioni finanziarie
 * Chiamate dirette via API Gateway (9001) per performance in produzione
 */
import axios from 'axios';
import i18next from 'i18next';

function getApiGatewayUrl() {
  const env = typeof window !== 'undefined' && window.env ? window.env : {};
  return env.REACT_APP_API_URL || 'http://localhost:9001';
}

function getAuthHeaders() {
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('access_token') : null;
  const userId = typeof window !== 'undefined' ? window.localStorage.getItem('user_id') : null;
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (userId) headers['X-User-Id'] = userId;
  const tenant = typeof window !== 'undefined' && window.env && window.env.REACT_APP_API_TENANT;
  if (tenant) headers.TENANT_ID = tenant;
  headers['X-Client-Surface'] = 'FRONTEND';
  return headers;
}

// I percorsi del credit-service come è oggi. Quelli precedenti — /wallet/... —
// appartenevano a un servizio riscritto da allora: è il frontend ad allinearsi
// a ciò che la piattaforma espone, non il contrario.
const CREDIT_SERVICE_BASE = '/private/credit';

/**
 * Ottieni wallet di un utente
 * @param {string} userId - ID utente
 * @returns {Promise<Object>} Wallet con saldi e fido
 */
/**
 * Avvisa l'applicazione che il saldo è cambiato.
 *
 * Sta qui e non nelle schermate perché tutte le operazioni sul denaro passano
 * di qui: chi aggiunge una funzione domani non deve ricordarsi nulla.
 */
function notifyWalletChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('wallet:changed'));
  }
}

export async function getWallet(userId) {
  const base = getApiGatewayUrl();
  const url = `${base}${CREDIT_SERVICE_BASE}/${userId}/figures`;
  const res = await axios.get(url, { headers: getAuthHeaders() });
  return res.data;
}

/**
 * Crea wallet per un utente (se non esiste)
 * @param {string} userId - ID utente
 * @param {string} currency - Valuta (default: EUR)
 * @returns {Promise<Object>} Wallet creato
 */
export async function createWallet(userId, currency = 'EUR') {
  const base = getApiGatewayUrl();
  const url = `${base}${CREDIT_SERVICE_BASE}/${userId}`;
  const res = await axios.post(url, { currency }, { headers: getAuthHeaders() });

  notifyWalletChanged();
  return res.data;
}

/**
 * Esegui operazione giroconto (trasferimento saldo)
 * @param {Object} params
 * @param {string} params.toUserId - ID utente destinatario
 * @param {number} params.amount - Importo
 * @param {string} params.causale - Causale operazione
 * @param {string} [params.note] - Note aggiuntive
 * @returns {Promise<Object>} Risultato operazione
 */
export async function giroconto({ fromUserId, toUserId, amount, causale, note, operation }) {
  const base = getApiGatewayUrl();
  const url = `${base}${CREDIT_SERVICE_BASE}/transfer`;

  const res = await axios.post(url, {
    fromUserId: fromUserId || currentUserId(),
    toUserId,
    amount,
    // Il VERSO: senza, il servizio non puo' comporre "Versamento a X" o
    // "Prelievo da Y" e nello storico resta scritto "Giroconto", che non dice
    // niente a chi lo legge. Lo sa solo chi apre il modulo.
    operation,
    // il servizio tiene una descrizione sola, che finisce nello storico di
    // entrambi i conti
    description: [causale, note].filter(Boolean).join(' — ') || undefined,
  }, { headers: getAuthHeaders() });


  notifyWalletChanged();
  return res.data;
}

/** Chi sta operando, letto dal token. */
function currentUserId() {
  const token = typeof window !== 'undefined'
      ? window.localStorage.getItem('access_token') : null;
  if (!token) return null;
  try {
    const payload = JSON.parse(
        atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.userId || payload.sub || null;
  } catch (e) {
    return null;
  }
}

/**
 * Esegui operazione fido (assegnazione/restituzione)
 * @param {Object} params
 * @param {string} params.toUserId - ID utente destinatario
 * @param {number} params.amount - Importo fido
 * @param {string} params.operazione - 'assegna' o 'restituisci'
 * @param {string} params.causale - Causale operazione
 * @param {string} [params.note] - Note aggiuntive
 * @returns {Promise<Object>} Risultato operazione
 */
export async function fido({ toUserId, amount, causale, note }) {
  const base = getApiGatewayUrl();
  const url = `${base}${CREDIT_SERVICE_BASE}/${toUserId}/credit`;

  // Il fido è un tetto che si imposta, non una somma da aggiungere: portarlo da
  // 1000 a 2000 lascia 2000. Per questo è una PUT — si sostituisce il valore —
  // e per questo non serve dire se si sta assegnando o togliendo: lo dice il
  // numero stesso rispetto a quello attuale.
  const res = await axios.put(url, {
    amount,
    fromUserId: currentUserId(),
    description: [causale, note].filter(Boolean).join(' — ') || undefined,
  }, { headers: getAuthHeaders() });


  notifyWalletChanged();
  return res.data;
}

/**
 * Esegui operazione bonus
 * @param {Object} params
 * @param {string} params.toUserId - ID utente destinatario
 * @param {number} params.amount - Importo bonus
 * @param {string} params.operazione - 'assegna' o 'rimuovi'
 * @param {string} params.causale - Causale operazione
 * @param {string} [params.note] - Note aggiuntive
 * @returns {Promise<Object>} Risultato operazione
 */
export async function bonus({ toUserId, amount, operazione, causale, note }) {
  const base = getApiGatewayUrl();
  const url = `${base}${CREDIT_SERVICE_BASE}/${toUserId}/movements`;
  const res = await axios.post(url, {
    amount,
    type: 'BONUS',
    description: [causale, note].filter(Boolean).join(' — ') || undefined,
  }, { headers: getAuthHeaders() });

  notifyWalletChanged();
  return res.data;
}

/**
 * Registra vincita su wallet utente
 * @param {Object} params
 * @param {string} params.userId - ID utente
 * @param {number} params.amount - Importo vincita
 * @param {string} params.referenceId - ID ticket/giocata
 * @param {string} [params.note] - Note aggiuntive
 * @returns {Promise<Object>} Risultato operazione
 */
export async function vincita({ userId, amount, referenceId, note }) {
  const base = getApiGatewayUrl();
  const url = `${base}${CREDIT_SERVICE_BASE}/${userId}/movements`;
  const res = await axios.post(url, {
    amount: Math.abs(amount),
    type: 'BET_WIN',
    referenceId,
    description: note || undefined,
  }, { headers: getAuthHeaders() });

  notifyWalletChanged();
  return res.data;
}

/**
 * Registra giocata (sottrae importo da wallet)
 * @param {Object} params
 * @param {string} params.userId - ID utente
 * @param {number} params.amount - Importo giocata
 * @param {string} params.referenceId - ID ticket
 * @param {string} [params.note] - Note aggiuntive
 * @returns {Promise<Object>} Risultato operazione
 */
export async function giocata({ userId, amount, referenceId, note }) {
  const base = getApiGatewayUrl();
  const url = `${base}${CREDIT_SERVICE_BASE}/${userId}/movements`;
  const res = await axios.post(url, {
    amount: -Math.abs(amount),
    type: 'BET_STAKE',
    referenceId,
    description: note || undefined,
  }, { headers: getAuthHeaders() });

  notifyWalletChanged();
  return res.data;
}

/**
 * Ottieni movimenti di un utente
 * @param {Object} params
 * @param {string} [params.userId] - ID utente
 * @param {string} [params.type] - Tipo movimento
 * @param {string} [params.dateFrom] - Data inizio
 * @param {string} [params.dateTo] - Data fine
 * @param {number} [params.page=0] - Pagina
 * @param {number} [params.limit=20] - Risultati per pagina
 * @returns {Promise<Object>} Lista movimenti con paginazione
 */
/** Cosa è successo, detto come lo direbbe una persona. */
function descriviMovimento(m) {
  const chi = m.counterpartyName;

  // Chi ha avviato l'operazione cambia come si legge la riga. Un giroconto in
  // uscita dal proprio conto è "ho versato a X" se l'ho fatto io, ma è
  // "prelievo da parte di X" se è stato X a prendere. Dire "versamento" quando
  // il tipo dice prelievo è una contraddizione che confonde chi legge.
  const iniziataDaMe = m.performedBy && m.userId && m.performedBy === m.userId;

  switch (m.type) {
    case 'TRANSFER_IN':
      if (!chi) return 'Versamento ricevuto';
      return iniziataDaMe ? i18next.t('Prelievo da {{chi}}', {chi}) : i18next.t('Versamento ricevuto da {{chi}}', {chi});

    case 'TRANSFER_OUT':
      if (!chi) return 'Versamento effettuato';
      return iniziataDaMe ? `Versamento a ${chi}` : i18next.t('Prelievo da parte di {{chi}}', {chi});

    case 'CREDIT_GRANT':
      return chi ? i18next.t('Fido ricevuto da {{chi}}', {chi}) : 'Fido assegnato';
    case 'CREDIT_REVOKE':
      return chi ? i18next.t('Fido ridotto da {{chi}}', {chi}) : 'Fido ridotto';
    case 'DEPOSIT':
      return chi ? i18next.t('Versamento in cassa da {{chi}}', {chi}) : 'Versamento in cassa';
    case 'WITHDRAWAL':
      return chi ? i18next.t('Prelievo da {{chi}}', {chi}) : 'Prelievo';
    case 'CRYPTO_DEPOSIT':
      return 'Versamento in criptovaluta';
    case 'CRYPTO_WITHDRAWAL':
      return 'Prelievo in criptovaluta';
    case 'CRYPTO_WITHDRAWAL_REFUND':
      return 'Prelievo in criptovaluta restituito';
    case 'ADJUSTMENT':
      return chi ? i18next.t('Rettifica da {{chi}}', {chi}) : 'Rettifica';
    case 'BET_STAKE':
      return 'Giocata';
    case 'BET_WIN':
      return 'Vincita';
    case 'BET_REFUND':
      return 'Rimborso giocata';
    case 'BET_REPROPOSE_REFUND':
      return 'Rimborso riproposta';
    case 'BET_CASHOUT':
      return 'Cash out della giocata';
    case 'BONUS_UNLOCK':
      return 'Bonus sbloccato';
    case 'COMMISSION':
      return 'Commissioni';
    default:
      return chi ? `${m.type} — ${chi}` : (m.description || m.type || '');
  }
}

/**
 * La descrizione, più la nota dell'operatore quando c'è.
 *
 * La frase la compone il SERVIZIO: "Giocata sport (ticket 9A84912B)",
 * "Versamento da agenzia12". Ricostruirla qui vorrebbe dire tenere la stessa
 * regola allineata a mano fra sito, backoffice ed estratto conto — e il giorno
 * che si aggiungono le slot bisognerebbe aggiornarli tutti e tre.
 *
 * descriviMovimento resta per i movimenti vecchi, scritti prima che il
 * servizio componesse la descrizione.
 */
function componiCausale(m) {
  const descrizione = (m.description || '').trim() || descriviMovimento(m);
  // Le commissioni della rete (Report › Accrediti e Rettifiche): il servizio
  // scrive la frase intera, «Addebito Commissioni Sport dal 31-08-2026 al
  // 27-09-2026», e basta quella. Le righe scritte prima del 5 novembre 2026
  // hanno invece «Commissioni (acc:…)» come descrizione e la frase vera nella
  // nota: si legge la nota. La chiave resta nella colonna del riferimento.
  if (m.type === 'COMMISSION') {
    return notaOperatore(m) || descrizione;
  }
  // Casino: al cliente basta il giro, che legge all'assistenza quando chiama.
  // La transazione del fornitore e la nota intera restano nel backoffice.
  if (diCasino(m)) {
    const giro = (m.roundId || '').trim();
    return giro ? `${descrizione} · giro ${giro}` : descrizione;
  }
  const nota = notaOperatore(m);
  return nota ? `${descrizione} (${nota})` : descrizione;
}

/** Un movimento del casino scritto col dettaglio del fornitore (gioco, giro, transazione). */
function diCasino(m) {
  return Boolean(m.gameName || m.roundId || m.providerTransactionId);
}

/**
 * Quello che ha scritto chi ha fatto l'operazione.
 *
 * Il servizio ora tiene la nota in un campo suo, separata dalla descrizione del
 * tipo: prima le due cose condividevano un campo solo e la nota faceva sparire
 * la parola "Giroconto".
 */
function notaOperatore(m) {
  return (m.operatorNote || '').trim();
}

export async function getMovements(params = {}) {
  const base = getApiGatewayUrl();

  // Senza un utente il percorso sarebbe rotto: i movimenti sono sempre di
  // qualcuno, e in mancanza d'altro sono i propri.
  const userId = params.userId || currentUserId();
  if (!userId) {
    return { movements: [], total: 0, page: 1, limit: 20, totalPages: 0 };
  }

  const url = `${base}${CREDIT_SERVICE_BASE}/${userId}/movements`;
  const res = await axios.get(url, { headers: getAuthHeaders() });

  const list = Array.isArray(res.data) ? res.data : [];

  // Il servizio parla di conti e movimenti, la pagina di righe e pagine: la
  // traduzione fra i due sta qui, in un punto solo.
  const movements = list.map((m) => {
    const importo = Number(m.amount) || 0;

    // La pagina ragiona in dare e avere, come un estratto conto: quello che
    // entra da una parte, quello che esce dall'altra. Il servizio tiene un
    // importo col segno, che è la stessa cosa detta in modo diverso.
    return {
      id: m.id,
      movementId: m.id,
      createdAt: m.createdAt,
      type: m.type,
      dare: importo < 0 ? Math.abs(importo) : 0,
      avere: importo > 0 ? importo : 0,
      importo,
      saldoDopo: m.balanceAfter,
      // La causale dice cosa è successo e con chi: senza il nome della
      // controparte una riga di estratto conto non serve a nulla.
      causale: componiCausale(m),
      causaleOriginale: m.description || '',
      controparte: m.counterpartyName || '',
      // Sul casino il codice da leggere all'assistenza e' il giro: il
      // riferimento e' una chiave interna che non dice niente a nessuno.
      riferimento: diCasino(m) ? (m.roundId || '') : m.referenceId,
      valuta: m.currency,
      data: m.createdAt,
      tipo: m.type,
      raw: m,
    };
  });

  // Filtri applicati qui: il servizio restituisce lo storico del conto, e
  // ridurlo in memoria evita di moltiplicare le varianti dell'endpoint.
  const filtered = movements.filter((m) => {
    if (params.dateFrom && new Date(m.data) < new Date(params.dateFrom)) return false;
    if (params.dateTo && new Date(m.data) > new Date(params.dateTo)) return false;
    if (params.type && params.type !== 'all') {
      // Entrata e uscita di un giroconto sono due tipi ma una cosa sola per chi
      // filtra: chiedendo "giroconto" si vogliono entrambe le direzioni.
      const coppie = {
        TRANSFER_IN: ['TRANSFER_IN', 'TRANSFER_OUT'],
        TRANSFER_OUT: ['TRANSFER_IN', 'TRANSFER_OUT'],
      };
      const ammessi = coppie[params.type] || [params.type];
      if (!ammessi.includes(m.tipo)) return false;
    }
    // Nome o causale. Cerca fra le righe scaricate, che oggi sono tutte:
    // il servizio manda lo storico intero e non ha un parametro di ricerca.
    // Quando ce l'avra', questo filtro sparisce e la domanda la fa il
    // servizio.
    if (params.search) {
      const ago = String(params.search).toLowerCase();
      const pagliaio = `${m.causale || ''} ${m.controparte || ''} ${m.riferimento || ''}`.toLowerCase();
      if (!pagliaio.includes(ago)) return false;
    }
    return true;
  });

  const page = Number(params.page) || 1;
  const limit = Number(params.limit) || 20;
  const from = (page - 1) * limit;

  return {
    movements: filtered.slice(from, from + limit),
    total: filtered.length,
    page,
    limit,
    totalPages: Math.max(1, Math.ceil(filtered.length / limit)),
    totals: totali(filtered),
  };
}

/* I TOTALI SONO SULLE RIGHE FILTRATE, NON SULLA PAGINA: chi guarda vuole
   sapere quanto e' entrato e uscito nel periodo che ha scelto, non nelle
   venti righe che sta vedendo.

   E il FIDO resta fuori: assegnare un fido non muove denaro, concede un
   limite. Sommarlo alle entrate gonfierebbe il conto di soldi che nessuno ha
   versato. E' la stessa regola del backoffice. */
const SENZA_DENARO = ['CREDIT_GRANT', 'CREDIT_REVOKE'];

function totali(righe) {
  const contate = righe.filter((m) => !SENZA_DENARO.includes(m.tipo));
  const entrate = contate.filter((m) => m.importo > 0)
      .reduce((s, m) => s + m.importo, 0);
  const uscite = contate.filter((m) => m.importo < 0)
      .reduce((s, m) => s + m.importo, 0);
  return {
    entrate,
    uscite,
    netto: entrate + uscite,      // uscite e' gia' negativo
    conteggio: righe.length,      // le righe si contano tutte, fido compreso
  };
}

export async function getMovementsByUser(userId, limit = 20) {
  const base = getApiGatewayUrl();
  const url = `${base}${CREDIT_SERVICE_BASE}/movements/user/${userId}`;
  const res = await axios.get(url, { params: { limit }, headers: getAuthHeaders() });
  return res.data;
}

/**
 * Ottieni disponibilità totale (saldo + fido disponibile)
 * @param {string} userId - ID utente
 * @returns {Promise<number>} Disponibilità totale
 */
export async function getDisponibilita(userId) {
  const wallet = await getWallet(userId);
  return wallet?.disponibilita || 0;
}

/**
 * Verifica se utente ha saldo sufficiente per giocata
 * @param {string} userId - ID utente
 * @param {number} amount - Importo richiesto
 * @returns {Promise<{canPlay: boolean, disponibilita: number, message: string}>}
 */
export async function checkBalance(userId, amount) {
  const wallet = await getWallet(userId);
  const disponibilita = wallet?.disponibilita || 0;
  const canPlay = disponibilita >= amount;
  return {
    canPlay,
    disponibilita,
    message: canPlay ? i18next.t('Saldo sufficiente') : `Saldo insufficiente. Disponibile: €${disponibilita.toFixed(2)}`
  };
}

const creditApi = {
  getWallet,
  createWallet,
  giroconto,
  fido,
  bonus,
  vincita,
  giocata,
  getMovements,
  getMovementsByUser,
  getDisponibilita,
  checkBalance
};

export default creditApi;
